import qs from 'qs';
import { term } from 'lucene';
import { Filter, GroupFilterMode, SearchQuery, Sort } from './types';

export const escapeSearchSpecialCharacters = (value: string): string => term.escape(value);

export const getFilterString = (filter: any): string[] => {
  const filterDelimiter = ':';

  return Object.getOwnPropertyNames(filter).flatMap((prop) => {
    let value = filter[prop];
    if (prop === 'ages') {
      return [`${value}`];
    }
    if (prop === 'languages.audio' && typeof value === 'string') {
      value = value.split(',');
    }
    if (Array.isArray(value)) {
      return (value as string[]).map((val) => `${prop}${filterDelimiter}${val}`);
    }

    return [`${prop}${filterDelimiter}${value}`];
  });
};

export const getQueryString = (params: SearchQuery): string => {
  let filter: any = [];

  if (params?.filter !== undefined) {
    // JSON.parse(JSON.stringify()) is to remove the observer object from the list that vue store adds
    filter = [...filter, ...getFilterString(JSON.parse(JSON.stringify(params.filter)))];
  }

  const query = {
    q: params.q,
    searchFields: params.searchFields,
    filter,
    sort: params.sort?.map((fieldSortParam) => `${fieldSortParam.field}:${fieldSortParam.order ?? 'desc'}`),
    pagination: params.pagination ?? { size: 20 },
    groupFilterMode: params.groupFilterMode,
  };

  const urlQs = qs.stringify(query, { arrayFormat: 'repeat' });

  return urlQs ? `?${urlQs}` : '';
};

const getSortFromSortString = (sortString: string): Sort => {
  const [field, order] = sortString.split(':');

  if (!field || !order) {
    throw new Error(`Invalid sort string: ${sortString}`);
  }
  return { field, order: order as 'asc' | 'desc' };
};

export const getQueryObject = (query: string): SearchQuery => {
  const parsed = qs.parse(query);
  let filter = {};
  if (parsed.filter !== undefined) {
    const filterString = (parsed.filter as any).map((pair: string) => {
      if (pair.startsWith('age(')) {
        return ['ages', pair];
      }

      // In case there is a  ":" in the value, we need to split the string at the first occurrence of ":"
      const firstColonIndex = pair.indexOf(':');
      const key = pair.substring(0, firstColonIndex);
      const value = pair.substring(firstColonIndex + 1);
      return [key, value];
    });
    filter = Object.fromEntries(filterString);
  }

  const sort: Sort[] = [];
  if (parsed.sort) {
    if (Array.isArray(parsed.sort)) {
      sort.push(
        ...(parsed.sort as string[]).map((sortString: string) => {
          return getSortFromSortString(sortString);
        }),
      );
    } else if (typeof parsed.sort === 'string') {
      sort.push(getSortFromSortString(parsed.sort));
    }
  }
  return { ...parsed, filter, sort: sort.length > 0 ? sort : undefined };
};

export const getAggregateString = (
  aggregateFields: string[],
  globalFilters: Filter,
  size?: number,
  query?: string,
  groupFilterMode?: GroupFilterMode,
) => {
  let filter: any = [];

  if (globalFilters !== undefined) {
    // JSON.parse(JSON.stringify()) is to remove the observer object from the list that vue store adds
    filter = [...filter, ...getFilterString(JSON.parse(JSON.stringify(globalFilters)))];
  }
  const urlQs = qs.stringify(
    { filter, dim: aggregateFields, size, q: query, groupFilterMode },
    { arrayFormat: 'repeat' },
  );
  return urlQs ? `?${urlQs}` : '';
};
